import React, { useState } from "react";
import { motion } from "framer-motion";
import { Section } from "@/components/Section";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PACKAGES, SCHOOL } from "@/lib/constants";
import {
  Phone,
  MessageCircle,
  Star,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Card colors hardcoded to the original warm palette (matches the screenshot reference),
// kept independent of the site-wide Apple Frost theme.
//   tan card:    bg #D4B896 with #4E321A mocha text
//   navy card:   bg #1B2A4E with #F5EFE0 cream text
//   brown card:  bg #6B4423 with #F5EFE0 cream text
// Hex values are spelled out as literals so Tailwind JIT can generate the arbitrary utilities.
const variantStyles = {
  tan: {
    surface: "bg-[#D4B896] border border-[#4E321A]/15 text-[#4E321A]",
    track: "text-[#4E321A]",
    badge: "bg-[#4E321A] text-[#F5EFE0]",
    accentText: "text-[#4E321A]",
    title: "text-[#4E321A]",
    body: "text-[#4E321A]/85",
    price: "text-[#4E321A]",
    divider: "bg-[#4E321A]/25",
    bullet: "bg-[#4E321A]",
    chip: "border-[#4E321A]/25 bg-[#FBF7EC] text-[#4E321A]",
    primaryBtn: "bg-[#4E321A] text-[#F5EFE0] hover:bg-[#6B4423]",
    outlineBtn: "border-[#4E321A]/40 text-[#4E321A] hover:bg-[#4E321A] hover:text-[#F5EFE0]",
    learnMore: "text-[#4E321A] hover:text-[#6B4423]",
  },
  navy: {
    surface: "bg-[#1B2A4E] text-[#F5EFE0]",
    track: "text-[#F5EFE0]/85",
    badge: "bg-[#F5EFE0] text-[#1B2A4E]",
    accentText: "text-[#F5EFE0]",
    title: "text-[#F5EFE0]",
    body: "text-[#F5EFE0]/85",
    price: "text-[#F5EFE0]",
    divider: "bg-[#F5EFE0]/25",
    bullet: "bg-[#F5EFE0]",
    chip: "border-[#F5EFE0]/25 bg-[#F5EFE0]/10 text-[#F5EFE0]",
    primaryBtn: "bg-[#F5EFE0] text-[#1B2A4E] hover:bg-[#FBF7EC]",
    outlineBtn: "border-[#F5EFE0]/40 text-[#F5EFE0] hover:bg-[#F5EFE0] hover:text-[#1B2A4E]",
    learnMore: "text-[#F5EFE0] hover:text-[#F5EFE0]/80",
  },
  brown: {
    surface: "bg-[#6B4423] text-[#F5EFE0]",
    track: "text-[#F5EFE0]/85",
    badge: "bg-[#F5EFE0] text-[#6B4423]",
    accentText: "text-[#F5EFE0]",
    title: "text-[#F5EFE0]",
    body: "text-[#F5EFE0]/85",
    price: "text-[#F5EFE0]",
    divider: "bg-[#F5EFE0]/25",
    bullet: "bg-[#F5EFE0]",
    chip: "border-[#F5EFE0]/25 bg-[#F5EFE0]/10 text-[#F5EFE0]",
    primaryBtn: "bg-[#F5EFE0] text-[#6B4423] hover:bg-[#FBF7EC]",
    outlineBtn: "border-[#F5EFE0]/40 text-[#F5EFE0] hover:bg-[#F5EFE0] hover:text-[#6B4423]",
    learnMore: "text-[#F5EFE0] hover:text-[#F5EFE0]/80",
  },

  // ----- G row: cool / natural palette -----

  // G-5 · pale lavender frost — blends with site Apple-Frost background
  frost: {
    surface: "bg-[#EFEEF8] border border-[#2C2C5C]/15 text-[#2C2C5C]",
    track: "text-[#2C2C5C]",
    badge: "bg-[#2C2C5C] text-[#F5F5F7]",
    accentText: "text-[#2C2C5C]",
    title: "text-[#2C2C5C]",
    body: "text-[#2C2C5C]/85",
    price: "text-[#2C2C5C]",
    divider: "bg-[#2C2C5C]/25",
    bullet: "bg-[#2C2C5C]",
    primaryBtn: "bg-[#2C2C5C] text-[#F5F5F7] hover:bg-[#3B3A75]",
    learnMore: "text-[#2C2C5C] hover:text-[#2C2C5C]/70",
  },
  // (legacy) G-5 · soft sage refresher — kept for revert
  sage: {
    surface: "bg-[#DDE7DA] border border-[#1F2D24]/15 text-[#1F2D24]",
    track: "text-[#1F2D24]",
    badge: "bg-[#1F2D24] text-[#F5F5F7]",
    accentText: "text-[#1F2D24]",
    title: "text-[#1F2D24]",
    body: "text-[#1F2D24]/85",
    price: "text-[#1F2D24]",
    divider: "bg-[#1F2D24]/25",
    bullet: "bg-[#1F2D24]",
    learnMore: "text-[#1F2D24] hover:text-[#1F2D24]/70",
  },
  // G-7 · soft sky blue (light bg, deep navy text)
  sky: {
    surface: "bg-[#D6E2F0] border border-[#1B2E4E]/15 text-[#1B2E4E]",
    track: "text-[#1B2E4E]",
    badge: "bg-[#1B2E4E] text-[#F5F5F7]",
    accentText: "text-[#1B2E4E]",
    title: "text-[#1B2E4E]",
    body: "text-[#1B2E4E]/85",
    price: "text-[#1B2E4E]",
    divider: "bg-[#1B2E4E]/25",
    bullet: "bg-[#1B2E4E]",
    primaryBtn: "bg-[#1B2E4E] text-[#F5F5F7] hover:bg-[#263D66]",
    learnMore: "text-[#1B2E4E] hover:text-[#1B2E4E]/70",
  },
  // G-10 · soft peach cream (light bg, deep mocha text)
  peachCream: {
    surface: "bg-[#F2E1D0] border border-[#3D2817]/15 text-[#3D2817]",
    track: "text-[#3D2817]",
    badge: "bg-[#3D2817] text-[#F5F5F7]",
    accentText: "text-[#3D2817]",
    title: "text-[#3D2817]",
    body: "text-[#3D2817]/85",
    price: "text-[#3D2817]",
    divider: "bg-[#3D2817]/25",
    bullet: "bg-[#3D2817]",
    primaryBtn: "bg-[#3D2817] text-[#F5F5F7] hover:bg-[#5A3A21]",
    learnMore: "text-[#3D2817] hover:text-[#3D2817]/70",
  },

  // ----- Tier row: metallic palette (matches Silver / Gold / Platinum names) -----

  // Silver · cool light gray (light bg, slate text)
  silver: {
    surface: "bg-[#D5D9E0] border border-[#1F2937]/15 text-[#1F2937]",
    track: "text-[#1F2937]",
    badge: "bg-[#1F2937] text-[#F5F5F7]",
    accentText: "text-[#1F2937]",
    title: "text-[#1F2937]",
    body: "text-[#1F2937]/85",
    price: "text-[#1F2937]",
    divider: "bg-[#1F2937]/25",
    bullet: "bg-[#1F2937]",
    primaryBtn: "bg-[#1F2937] text-[#F5F5F7] hover:bg-[#374151]",
    learnMore: "text-[#1F2937] hover:text-[#1F2937]/70",
  },
  // Gold · warm champagne (light bg, mocha text)
  gold: {
    surface: "bg-[#DCC084] border border-[#3D2817]/15 text-[#3D2817]",
    track: "text-[#3D2817]",
    badge: "bg-[#3D2817] text-[#F5F5F7]",
    accentText: "text-[#3D2817]",
    title: "text-[#3D2817]",
    body: "text-[#3D2817]/85",
    price: "text-[#3D2817]",
    divider: "bg-[#3D2817]/25",
    bullet: "bg-[#3D2817]",
    primaryBtn: "bg-[#3D2817] text-[#F5F5F7] hover:bg-[#5A3A21]",
    learnMore: "text-[#3D2817] hover:text-[#3D2817]/70",
  },
  // Platinum · charcoal (very dark bg, cream text — top tier feel)
  charcoal: {
    surface: "bg-[#1A1D29] text-[#F5F5F7]",
    track: "text-[#F5F5F7]/85",
    badge: "bg-[#F5F5F7] text-[#1A1D29]",
    accentText: "text-[#F5F5F7]",
    title: "text-[#F5F5F7]",
    body: "text-[#F5F5F7]/85",
    price: "text-[#F5F5F7]",
    divider: "bg-[#F5F5F7]/25",
    bullet: "bg-[#F5F5F7]",
    primaryBtn: "bg-[#F5F5F7] text-[#1A1D29] hover:bg-[#FFFFFF]",
    learnMore: "text-[#F5F5F7] hover:text-[#F5F5F7]/80",
  },

  // ----- BDE row: purple palette (matches site-wide iOS purple accent) -----

  // BDE course · soft butter cream (light bg, deep mocha text)
  butterCream: {
    surface: "bg-[#F5EED4] border border-[#4A3318]/15 text-[#4A3318]",
    track: "text-[#4A3318]",
    badge: "bg-[#4A3318] text-[#F5F5F7]",
    accentText: "text-[#4A3318]",
    title: "text-[#4A3318]",
    body: "text-[#4A3318]/85",
    price: "text-[#4A3318]",
    divider: "bg-[#4A3318]/25",
    bullet: "bg-[#4A3318]",
    primaryBtn: "bg-[#4A3318] text-[#F5F5F7] hover:bg-[#6B4423]",
    learnMore: "text-[#4A3318] hover:text-[#4A3318]/70",
  },
  // (legacy) BDE course · soft teal mist — kept for revert
  tealMist: {
    surface: "bg-[#D4E7E4] border border-[#0F3D38]/15 text-[#0F3D38]",
    track: "text-[#0F3D38]",
    badge: "bg-[#0F3D38] text-[#F5F5F7]",
    accentText: "text-[#0F3D38]",
    title: "text-[#0F3D38]",
    body: "text-[#0F3D38]/85",
    price: "text-[#0F3D38]",
    divider: "bg-[#0F3D38]/25",
    bullet: "bg-[#0F3D38]",
    learnMore: "text-[#0F3D38] hover:text-[#0F3D38]/70",
  },
  // (legacy) BDE course · soft mint — kept for revert
  mintSoft: {
    surface: "bg-[#E0EBE2] border border-[#1F2D24]/15 text-[#1F2D24]",
    track: "text-[#1F2D24]",
    badge: "bg-[#1F2D24] text-[#F5F5F7]",
    accentText: "text-[#1F2D24]",
    title: "text-[#1F2D24]",
    body: "text-[#1F2D24]/85",
    price: "text-[#1F2D24]",
    divider: "bg-[#1F2D24]/25",
    bullet: "bg-[#1F2D24]",
    learnMore: "text-[#1F2D24] hover:text-[#1F2D24]/70",
  },
  // (legacy) BDE course · soft lavender — kept for revert
  lavender: {
    surface: "bg-[#C7C4F0] border border-[#2A1B6E]/15 text-[#2A1B6E]",
    track: "text-[#2A1B6E]",
    badge: "bg-[#2A1B6E] text-[#F5F5F7]",
    accentText: "text-[#2A1B6E]",
    title: "text-[#2A1B6E]",
    body: "text-[#2A1B6E]/85",
    price: "text-[#2A1B6E]",
    divider: "bg-[#2A1B6E]/25",
    bullet: "bg-[#2A1B6E]",
    learnMore: "text-[#2A1B6E] hover:text-[#2A1B6E]/70",
  },
  // BDE course · deep iOS purple (dark purple bg, white text)
  iosPurple: {
    surface: "bg-[#3F3DAB] text-[#FFFFFF]",
    track: "text-[#FFFFFF]/85",
    badge: "bg-[#FFFFFF] text-[#3F3DAB]",
    accentText: "text-[#FFFFFF]",
    title: "text-[#FFFFFF]",
    body: "text-[#FFFFFF]/85",
    price: "text-[#FFFFFF]",
    divider: "bg-[#FFFFFF]/25",
    bullet: "bg-[#FFFFFF]",
    primaryBtn: "bg-[#FFFFFF] text-[#3F3DAB] hover:bg-[#F5F5F7]",
    learnMore: "text-[#FFFFFF] hover:text-[#FFFFFF]/80",
  },
};

const LearnMoreDialog = ({ pkg, open, onOpenChange }) => {
  if (!pkg) return null;
  const lm = pkg.learnMore || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-hidden p-0 border-0 bg-white/80 backdrop-blur-2xl backdrop-saturate-150 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] rounded-3xl"
        data-testid={`learn-more-dialog-${pkg.id}`}
      >
        <DialogTitle className="sr-only">
          {pkg.displayTitle || `${pkg.lessons}-lesson ${pkg.track} package`} details
        </DialogTitle>
        <DialogDescription className="sr-only">
          Detailed breakdown of what is included and why to choose this package.
        </DialogDescription>

        <div className="max-h-[88vh] overflow-y-auto px-6 sm:px-8 pt-8 pb-8 space-y-7">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold">
              <span className="inline-flex items-center rounded-full bg-maple-tan2/10 text-maple-tan2 ring-1 ring-maple-tan2/30 px-2.5 py-1">
                {pkg.trackLabel || (pkg.track === "BDE" ? "B.D.E. Course" : `${pkg.track} Track`)}
              </span>
              {pkg.popular ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-maple-navy text-white px-2.5 py-1">
                  <Star className="h-3 w-3" aria-hidden="true" />{" "}
                  {typeof pkg.popular === "string" ? pkg.popular : "Most popular"}
                </span>
              ) : null}
            </div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-maple-navy tracking-[-0.02em] leading-[1.1]">
              {pkg.displayTitle || `${pkg.lessons}-Lesson ${pkg.track} Package`}
            </h2>
            {pkg.displaySubtitle ? (
              <p className="mt-1 text-sm text-maple-navy/65">{pkg.displaySubtitle}</p>
            ) : null}
            {pkg.audience ? (
              <p className="mt-2 text-xs uppercase tracking-[0.14em] font-semibold text-maple-tan2">
                {pkg.audience}
              </p>
            ) : null}
            {pkg.prices && pkg.prices.length ? (
              <div className="mt-5">
                <div className="space-y-1.5">
                  {pkg.prices.map((p) => (
                    <div key={p.track} className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-serif text-3xl sm:text-4xl font-semibold uppercase tracking-[0.04em] text-maple-tan2 w-14">
                        {p.track}
                      </span>
                      {p.original ? (
                        <span className="font-serif text-2xl sm:text-3xl text-maple-navy/55 line-through tracking-[-0.02em] leading-none">
                          ${p.original}
                        </span>
                      ) : null}
                      <span className="font-serif text-4xl sm:text-5xl text-maple-navy tracking-[-0.02em] leading-none">
                        ${p.value}{p.currency ? ` ${p.currency}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-sm text-maple-navy/60">
                  {pkg.priceNote || "All package prices are subject to applicable taxes."}
                </p>
              </div>
            ) : (
              <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                {pkg.original ? (
                  <span className="font-serif text-2xl sm:text-3xl text-maple-navy/55 line-through tracking-[-0.02em] leading-none">
                    ${pkg.original}
                  </span>
                ) : null}
                <span className="font-serif text-5xl sm:text-6xl text-maple-navy tracking-[-0.02em] leading-none">
                  ${pkg.price}
                </span>
                <span className="text-sm text-maple-navy/60">
                  {pkg.priceNote || "All package prices are subject to applicable taxes."}
                </span>
              </div>
            )}
          </div>

          {/* Intro */}
          {lm.intro ? (
            Array.isArray(lm.intro) ? (
              <div className="space-y-3">
                {lm.intro.map((paragraph, i) => (
                  <p key={i} className="text-[15px] leading-relaxed text-maple-navy/85">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-[15px] leading-relaxed text-maple-navy/85">
                {lm.intro}
              </p>
            )
          ) : null}

          {/* What's included */}
          {lm.included && lm.included.length ? (
            <div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-maple-tan2" aria-hidden="true" />
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-maple-navy">
                  What's included
                </h3>
              </div>
              <ul className="mt-3 space-y-2.5">
                {lm.included.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-[14px] leading-relaxed text-maple-navy/90"
                  >
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-maple-tan2 shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Why choose */}
          {lm.whyChoose && lm.whyChoose.length ? (
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-maple-navy">
                  Why choose this package
                </h3>
              </div>
              <ul className="mt-3 space-y-2.5">
                {lm.whyChoose.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-[14px] leading-relaxed text-maple-navy/90"
                  >
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-maple-tan2 shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* CTAs */}
          <div className="pt-2 flex flex-col sm:flex-row gap-2">
            <Button
              asChild
              className="lift-on-hover h-11 flex-1 bg-maple-tan2 text-white hover:bg-maple-brown2"
              data-testid={`learn-more-call-${pkg.id}`}
            >
              <a href={SCHOOL.phoneLink}>
                <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                Call to book
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="lift-on-hover h-11 flex-1 border-maple-tan2/40 text-maple-tan2 hover:bg-maple-tan2/10"
              data-testid={`learn-more-whatsapp-${pkg.id}`}
            >
              <a href={SCHOOL.whatsappLink} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                WhatsApp us
              </a>
            </Button>
          </div>

          <p className="text-[11px] text-maple-navy/55 leading-relaxed">
            Payment is required in advance and is non-refundable, but it is transferrable to a
            friend or family member.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PackageCard = ({ pkg, idx, onLearnMore }) => {
  const v = variantStyles[pkg.bg] || variantStyles.tan;

  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: idx * 0.07, ease: EASE }}
      data-testid={`package-card-${pkg.id}`}
      className={`pricing-card relative flex flex-col h-full overflow-hidden rounded-3xl p-6 sm:p-7 ${v.surface}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${v.badge}`}
            data-testid={`package-track-${pkg.id}`}
          >
            {pkg.trackLabel || (pkg.track === "BDE" ? "B.D.E. Course" : `${pkg.track} Track`)}
          </span>
          {pkg.displayTitle ? (
            <h3 className={`mt-3 font-serif text-2xl sm:text-[26px] leading-tight ${v.title}`}>
              {pkg.displayTitle}
            </h3>
          ) : (
            <h3 className={`mt-3 font-serif leading-none ${v.title}`}>
              <span className="text-5xl sm:text-6xl font-semibold tracking-tight">{pkg.lessons}</span>
              <span className="ml-2 text-xl sm:text-2xl align-middle">In-Car Lessons</span>
            </h3>
          )}
          <p className={`mt-1 text-sm ${v.body}`}>
            {pkg.displaySubtitle ||
              (pkg.track === "BDE"
                ? "Beginner Driver Education course"
                : `Includes car for ${pkg.track} road test`)}
          </p>
        </div>
        {pkg.popular ? (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap ${v.badge}`}
            data-testid={`package-popular-${pkg.id}`}
          >
            <Star className="h-3 w-3" aria-hidden="true" />{" "}
            {typeof pkg.popular === "string" ? pkg.popular : "Popular"}
          </span>
        ) : null}
      </div>

      {pkg.audience ? (
        <p
          className={`mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] ${v.accentText}`}
          data-testid={`package-audience-${pkg.id}`}
        >
          {pkg.audience}
        </p>
      ) : null}

      {pkg.prices && pkg.prices.length ? (
        <div className="mt-3 space-y-1" data-testid={`package-prices-${pkg.id}`}>
          {pkg.prices.map((p) => (
            <div key={p.track} className="flex items-baseline gap-3 flex-wrap">
              <span className={`font-serif text-3xl sm:text-4xl font-semibold uppercase tracking-[0.04em] ${v.accentText} w-14`}>
                {p.track}
              </span>
              {p.original ? (
                <span className={`font-serif text-2xl sm:text-3xl leading-none tracking-[-0.02em] line-through opacity-60 ${v.price}`}>
                  ${p.original}
                </span>
              ) : null}
              <span className={`font-serif text-4xl sm:text-5xl leading-none tracking-[-0.02em] ${v.price}`}>
                ${p.value}{p.currency ? ` ${p.currency}` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex items-baseline gap-3 flex-wrap">
          {pkg.original ? (
            <span className={`font-serif text-2xl sm:text-3xl leading-none tracking-[-0.02em] line-through opacity-60 ${v.price}`}>
              ${pkg.original}
            </span>
          ) : null}
          <span className={`font-serif text-5xl sm:text-6xl leading-none tracking-[-0.02em] ${v.price}`}>
            ${pkg.price}
          </span>
        </div>
      )}

      {pkg.priceNote ? (
        <p className={`mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] ${v.accentText}`}>
          {pkg.priceNote}
        </p>
      ) : null}

      {pkg.bestFor ? (
        <p className={`mt-4 text-sm leading-relaxed ${v.body}`}>
          <span className={`font-semibold ${v.title}`}>Best for: </span>
          {pkg.bestFor}
        </p>
      ) : null}

      <div className={`mt-5 h-px w-full ${v.divider}`} aria-hidden="true" />

      <ul className="mt-5 space-y-2.5">
        {pkg.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
            <span className={`mt-1.5 inline-block h-1.5 w-1.5 rounded-full ${v.bullet}`} aria-hidden="true" />
            <span className={v.body}>{h}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <Button
          asChild
          className={`lift-on-hover h-11 rounded-full px-4 text-sm font-semibold ${v.primaryBtn || variantStyles.tan.primaryBtn}`}
          data-testid={`package-whatsapp-${pkg.id}`}
        >
          <a
            href={SCHOOL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Book ${pkg.displayTitle || pkg.trackLabel || "package"} on WhatsApp`}
          >
            <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
            Book on WhatsApp
          </a>
        </Button>
        <button
          type="button"
          onClick={() => onLearnMore(pkg)}
          className={`inline-flex justify-center sm:justify-start items-center gap-1.5 text-sm font-semibold ${v.learnMore} transition-colors`}
          data-testid={`package-learn-more-${pkg.id}`}
        >
          Learn more <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

    </motion.article>
  );
};

export const Packages = () => {
  const roadPackages = PACKAGES.filter((p) => p.track === "ROAD");
  const bdePackages = PACKAGES.filter((p) => p.track === "BDE");

  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const openLearnMore = (pkg) => {
    setActive(pkg);
    setOpen(true);
  };

  return (
    <Section id="packages" testId="section-packages">
      <div className="text-center" data-testid="packages-header">
        <span className="kicker">Packages</span>
      </div>

      {/* Road-test packages */}
      <div id="packages-road" className="mt-10 scroll-mt-24">
        <div className="flex items-center gap-3 mb-5">
          <span className="kicker">Our Packages</span>
          <span className="h-px flex-1 bg-maple-border" aria-hidden="true" />
        </div>
        <p className="text-sm text-maple-navy/75 max-w-3xl mb-6 leading-relaxed">
          Each package includes the listed lessons plus the $200 car for a local road test.
          Package 3, 4, 5, and 6 show the discounted total where applicable. Prices are plus tax.
        </p>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          data-testid="packages-grid-road"
        >
          {roadPackages.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} idx={i} onLearnMore={openLearnMore} />
          ))}
        </div>
      </div>

      {/* BDE course */}
      <div id="packages-bde" className="mt-12 scroll-mt-24">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-16 bg-maple-border" aria-hidden="true" />
          <span className="kicker">BDE Course</span>
          <span className="h-px w-16 bg-maple-border" aria-hidden="true" />
        </div>
        <div className="mx-auto max-w-xl" data-testid="packages-grid-bde">
          {bdePackages.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} idx={i} onLearnMore={openLearnMore} />
          ))}
        </div>
      </div>

      {/* Centered glass Learn More */}
      <LearnMoreDialog pkg={active} open={open} onOpenChange={setOpen} />
    </Section>
  );
};
